import { cn } from '@massilia/ui'
import { Search, SlidersHorizontal } from 'lucide-react'

import { useHideOnScrollDown } from '@/utils/use-hide-on-scroll-down'

import { BaggageQuickFilter } from './filters/baggage-quick-filter'

interface MobileResultsToolbarProps {
  activeFiltersCount: number
  /** Vrai tant qu'il n'y a pas d'offres à filtrer (chargement, erreur, zéro résultat). */
  filtersDisabled?: boolean
  onOpenFilters: () => void
  onOpenSearch: () => void
  /** Résumé de la recherche en cours, ex. « Marseille → Alger · 12 sept. · 2 voyageurs ». */
  summary?: string
}

/**
 * Barre d'actions collante des pages de résultats sur mobile, où le formulaire
 * de recherche n'est plus déplié en permanence : « Modifier la recherche » en
 * pleine largeur, puis les bagages et les filtres en demi-largeur.
 *
 * Elle s'escamote au défilement vers le bas et revient au défilement vers le
 * haut, pour laisser toute la hauteur aux cartes de vol.
 *
 * Le z-index reste modeste : les overlays plein écran qu'elle ouvre montent
 * bien plus haut (cf. `FullScreenOverlay`) et doivent la recouvrir.
 */
export function MobileResultsToolbar({
  activeFiltersCount,
  filtersDisabled = false,
  onOpenFilters,
  onOpenSearch,
  summary,
}: MobileResultsToolbarProps) {
  const isHidden = useHideOnScrollDown()

  return (
    <div
      className={cn(
        'sticky top-0 z-30 border-b border-gray-200 bg-white px-4 py-3 shadow-sm transition-transform duration-200 md:hidden',
        isHidden && '-translate-y-full'
      )}
    >
      <button
        className="flex h-12 w-full items-center gap-3 rounded-xl border border-input px-3 text-left transition-colors hover:bg-muted"
        onClick={onOpenSearch}
        type="button"
      >
        <Search aria-hidden className="size-4 shrink-0 text-brand-600" />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-gray-900">Modifier la recherche</span>
          {summary && <span className="block truncate text-xs text-gray-light-600">{summary}</span>}
        </span>
      </button>

      <div className="mt-2 grid grid-cols-2 gap-2">
        <BaggageQuickFilter disabled={filtersDisabled} />

        <button
          className="flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-input bg-white text-sm font-medium text-gray-900 transition-colors hover:bg-muted disabled:pointer-events-none disabled:opacity-50"
          disabled={filtersDisabled}
          onClick={onOpenFilters}
          type="button"
        >
          <SlidersHorizontal aria-hidden className="size-4 text-brand-600" />
          <span>Plus de filtres</span>
          {activeFiltersCount > 0 && (
            <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-brand-600 text-xs text-white">
              {activeFiltersCount}
            </span>
          )}
        </button>
      </div>
    </div>
  )
}
